// Bugsnag Version 6 docs:
// https://docs.bugsnag.com/platforms/javascript/legacy/reporting-handled-errors/

export enum CloudTeams {
  'AtlasI' = 'Atlas I',
  'AtlasIi' = 'Atlas II',
  'AtlasIii' = 'Atlas III',
  'AtlasGrowth' = 'Atlas Growth',
  'AtlasInternTeam' = 'Atlas Intern Team',
  'AtlasMigration' = 'Atlas Migration',
  'AtlasTriage' = 'Atlas Triage',
  'AtlasX' = 'Atlas X',
  'Automation' = 'Automation',
  'BackupAtlas' = 'Backup - Atlas',
  'BackupPrivateCloud' = 'Backup - Private Cloud',
  'CoreBillingAndIntegrations' = 'Core Billing and Integrations',
  'CoreIam' = 'Core IAM',
  'Design' = 'Design',
  'DeveloperProductivity' = 'Developer Productivity',
  'EngineeringManagement' = 'Engineering Management',
  'InsightsAndTelemetry' = 'Insights and Telemetry',
  'OpsManager' = 'Ops Manager',
  'Kubernetes' = 'Kubernetes',
  'ProgramManagement' = 'Program Management',
  'Search' = 'Search',
  'Security' = 'Security',
  'Sre' = 'SRE',
  'Triage' = 'Triage',
}

export interface BugsnagMetaData {
  team?: CloudTeams;
  [key: string]: any;
}

export type BugsnagReport = unknown; // Report object docs, in case anyone wants to fill 'em in: https://docs.bugsnag.com/platforms/javascript/legacy/customizing-error-reports/#the-report-object

export enum BugsnagSeverity {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
}

export interface BugsnagOptions {
  metaData?: BugsnagMetaData;
  severity?: BugsnagSeverity;
  user?: object;
  beforeSend?: (report: BugsnagReport) => void;
  context?: string;
  device?: object;
  request?: object;
}

export type BugsnagCallback = (err: Error, report: BugsnagReport) => void;
export interface BugsnagClient {
  notify: (error: Error, options?: BugsnagOptions, cb?: BugsnagCallback) => void;
}

type ErrorMaybe =
  | Error
  | {
      error?: Error;
      requestUrl?: string;
      errorCode?: string;
    };

export interface UseBugsnagNotifySpecs {
  error: ErrorMaybe;
  team: CloudTeams;
  metaData?: BugsnagMetaData;
  options?: BugsnagOptions;
  severity?: BugsnagSeverity;
}

interface GenericObject {
  [x: string]: any;
}

/**
 * Bugsnag only accepts Errors and strings as its error arg. In our fetchWrapper (and potentially elsewhere),
 * entities other than Errors are thrown.  This function will convert whatever is fed into sendError into an actual error.
 */
export const coerceError = (errorMaybe: ErrorMaybe): { error: Error; rawObject: GenericObject | null } => {
  // If coercion fails, we'll default to this error:
  let error: Error = new Error('Unknown error');
  let rawObject: GenericObject | null = null;

  if (errorMaybe instanceof Error) {
    error = errorMaybe;
  } else {
    // If an object was passed in rather than an error, we'll collect this to send along as metadata
    rawObject = errorMaybe;

    if (typeof errorMaybe !== 'object') {
      // Let the "Unknown error" pass through
    } else if ('error' in errorMaybe) {
      // fetchWrapper can generate objects with an error param in some cases.
      // The following shouldn't be necessary, but TS won't believe the nested error is defined unless we do this.
      const { error: nestedError } = errorMaybe;
      if (nestedError) {
        error = nestedError;
      }
    } else if ('requestUrl' in errorMaybe && 'errorCode' in errorMaybe) {
      // fetchWrapper can generate objects matching this signature:
      error = new Error(`Failed to reach ${errorMaybe.requestUrl} due to ${errorMaybe.errorCode}`);
    }

    // Message to Bugsnag user that additional context is available:
    error.message = `${error.message}. See metaData object for related information.`;
  }

  return { error, rawObject };
};

/**
 * A function that sends a properly-formed request to Bugsnag, and returns a promise
 * that resolves or rejects, dependent upon the success of the request.
 */
export function sendError({
  error: maybeError,
  team,
  metaData = {},
  severity = BugsnagSeverity.ERROR,
}: UseBugsnagNotifySpecs): Promise<void> {
  const browserErrorTrackingEnabled = (window as any).REQUEST_PARAMS?.browserErrorTrackingEnabled;
  if (!browserErrorTrackingEnabled) {
    return Promise.resolve();
  }
  // Convert non-errors into errors, so that Bugsnag can parse them:
  const { error, rawObject } = coerceError(maybeError);

  // If an object was passed in rather than an error, we'll collect this to send along as metadata:
  if (rawObject) {
    metaData.rawObject = rawObject;
  }

  // Ensure team is included in request:
  metaData.team = team;

  const options: BugsnagOptions = {
    metaData,
    severity,
  };

  // Return a promise which resolves when Bugsnag request responds
  return new Promise<void>((resolve, reject) => {
    if (!window.bugsnagClient) {
      // Alert that Bugsnag wasn't loaded when expected
      error.message = `Bugsnag not loaded! Original error message: ${error.message}`;
      console.error(error);
      reject(error);
    } else {
      // Send request to Bugsnag and resolve/reject upon response
      const cb = (err, report) => (err ? reject(err) : resolve(report));
      try {
        window.bugsnagClient.notify(error, options, cb);
      } catch (e) {
        reject(e);
      }
    }
  });
}
